import { useCalculateMeetingBillableTime, useSaveBillableTimeReason } from '@actions';
import { LoadingSpinner } from '@components/LoadingSpinner/LoadingSpinner';
import Button from '@components/V4/Button';
import TextArea from '@components/V4/Inputs/Textarea';
import Modal from '@components/V4/Modal/Modal';
import { zodResolver } from '@hookform/resolvers/zod';
import { fiftyMinutesInSeconds } from '@shared/common';
import {
  addBillableTimeReasonValidation,
  addBillableTimeReasonValidationFormData,
} from '@shared/validations';
import { convertSecondsToTime, mergeClassNames } from 'apps/agora/src/utils/helpers';
import { useForm } from 'react-hook-form';

interface BillableTimePageProps {
  meetingId: string;
  shouldCalculateBillableTime: boolean;
  onClose: () => void;
}

const BillableTimePage = (props: BillableTimePageProps) => {
  const { meetingId, shouldCalculateBillableTime, onClose } = props;

  const { data, isLoading: isBillableTimeProcessing } = useCalculateMeetingBillableTime(meetingId, {
    enabled: shouldCalculateBillableTime,
  });

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, touchedFields },
  } = useForm<addBillableTimeReasonValidationFormData>({
    resolver: zodResolver(addBillableTimeReasonValidation),
    reValidateMode: 'onBlur',
    mode: 'onBlur',
  });

  const { mutate: saveUserProfile } = useSaveBillableTimeReason(meetingId, {
    onSuccess: () => {
      onClose();
    },
  });

  const onSubmit = async (data: addBillableTimeReasonValidationFormData) => {
    const isValid = await trigger();
    if (isValid && data.billableTimeReason) {
      saveUserProfile({ billableTimeReason: data.billableTimeReason });
    } else {
      onClose();
    }
  };

  const submitHandler = (e: React.MouseEvent<HTMLButtonElement>) => handleSubmit(onSubmit)(e);

  return isBillableTimeProcessing ? (
    <LoadingSpinner />
  ) : (
    <Modal.Body className="tablet:px-0 gap-6 h-full">
      {shouldCalculateBillableTime ? (
        <>
          {data?.billableTime && data.billableTime !== null ? (
            <>
              <div className="flex flex-col gap-2 tablet:gap-0 tablet:flex-row tablet:justify-between tablet:items-center">
                <h3 className="text-customGrey text-2xl font-semibold">Billable Time</h3>
              </div>

              <div className="flex flex-col gap-3">
                <p>
                  The calculated billable time is:{' '}
                  <span
                    className={mergeClassNames('font-bold', {
                      'text-customPrimary': data.billableTime >= fiftyMinutesInSeconds,
                      'text-customRedAccent': data.billableTime < fiftyMinutesInSeconds,
                    })}
                  >
                    {data?.billableTime === 0 ? '0m' : convertSecondsToTime(data?.billableTime)}
                  </span>
                </p>

                {data?.billableTime < fiftyMinutesInSeconds && (
                  <div className="flex flex-col gap-2">
                    <p>
                      The session was shorter than 50 minutes, so it goes to{' '}
                      <span className="font-semibold text-white">pending review</span>. The team
                      reviews these sessions at the end of the month and decides how they are paid.
                    </p>
                    <p>
                      If the time is wrong because of a technical problem, for example the call
                      dropped or the timer did not count a reconnection, describe it below. This
                      comment is what the team sees during the review, so there is no need to write
                      on Discord or by email.
                    </p>

                    <TextArea
                      id={'billableTimeReason'}
                      label={'What happened (optional)'}
                      isTouched={touchedFields.billableTimeReason}
                      isValid={!errors.billableTimeReason}
                      errorText={errors.billableTimeReason?.message}
                      {...register('billableTimeReason')}
                    />
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2 tablet:gap-0 tablet:flex-row tablet:justify-between tablet:items-center">
                <h3 className="text-customGrey text-2xl font-semibold">
                  There was a problem calculating the billable time.
                </h3>
              </div>

              <p>
                The session goes to <span className="font-semibold text-white">pending review</span>{' '}
                and the team will check it at the end of the month. If you know what happened during
                the call, write it below so the reviewer has the context. No need to contact support
                separately.
              </p>

              <TextArea
                id={'billableTimeReason'}
                label={'What happened (optional)'}
                isTouched={touchedFields.billableTimeReason}
                isValid={!errors.billableTimeReason}
                errorText={errors.billableTimeReason?.message}
                {...register('billableTimeReason')}
              />
            </div>
          )}

          <div className="flex justify-end gap-4">
            <Button
              className="w-24"
              size="large"
              buttonText={
                !data?.billableTime || data.billableTime < fiftyMinutesInSeconds
                  ? 'Send to review'
                  : 'Close'
              }
              onClick={
                !data?.billableTime || data.billableTime < fiftyMinutesInSeconds
                  ? submitHandler
                  : () => onClose()
              }
            />
          </div>
        </>
      ) : (
        <p>Please submit the feedback first!</p>
      )}
    </Modal.Body>
  );
};

export default BillableTimePage;
